// ---------------------------------------------------------------------------
// Numeric stepper, for the host's settings.
//
// The visible squares are the handoff's 26px, but each one sits inside a 44px
// button: the host is often adjusting these on a phone, and a 26px target beside
// another 26px target is how a round timer ends up at 20 seconds by accident.
// ---------------------------------------------------------------------------

import type { ReactElement } from 'react';

export function Stepper(props: {
  label: string;
  value: number;
  onChange: (value: number) => void;
  min: number;
  max: number;
  step?: number;
  /** Renders the value, e.g. `30s`. Defaults to the bare number. */
  format?: (value: number) => string;
  disabled?: boolean;
  /** Off when the label is already rendered by the surrounding row. */
  showLabel?: boolean;
}): ReactElement {
  const {
    label,
    value,
    onChange,
    min,
    max,
    step = 1,
    format = (candidate: number) => String(candidate),
    disabled = false,
    showLabel = true,
  } = props;

  const nudge = (delta: number) => {
    const next = Math.min(max, Math.max(min, value + delta));
    if (next !== value) onChange(next);
  };

  return (
    <div className="flex items-center gap-3">
      {showLabel && <span className="text-[14px] font-medium text-ink-muted">{label}</span>}

      <div className="ml-auto flex items-center gap-1">
        <button
          type="button"
          aria-label={`decrease ${label}`}
          disabled={disabled || value <= min}
          onClick={() => nudge(-step)}
          className="grid h-11 w-11 place-items-center disabled:opacity-40"
        >
          <span className="grid h-[26px] w-[26px] place-items-center rounded-[7px] bg-raised text-[15px] font-semibold">
            &minus;
          </span>
        </button>

        <span
          aria-live="polite"
          className="min-w-12 text-center font-mono text-[17px] font-semibold text-blue-200 tabular"
        >
          {format(value)}
        </span>

        <button
          type="button"
          aria-label={`increase ${label}`}
          disabled={disabled || value >= max}
          onClick={() => nudge(step)}
          className="grid h-11 w-11 place-items-center disabled:opacity-40"
        >
          <span className="grid h-[26px] w-[26px] place-items-center rounded-[7px] bg-raised text-[15px] font-semibold">
            +
          </span>
        </button>
      </div>
    </div>
  );
}
